import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Building2, ChevronDown, CheckCircle2, ExternalLink } from "lucide-react";

// A bank connection synced within this window counts as "recently connected"
// and earns a small green pulse dot on the workspace switcher + Clients cards.
const RECENT_SYNC_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h

function isRecentlySynced(iso) {
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms <= RECENT_SYNC_WINDOW_MS;
}

/**
 * Compact switcher embedded in the sidebar workspace card.
 * Only renders when the user belongs to more than one workspace.
 */
export default function WorkspaceSwitcher() {
  const { tenant, setSession } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/memberships");
      setItems(data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("tenantChanged", onChange);
    return () => window.removeEventListener("tenantChanged", onChange);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (items.length <= 1) return null;

  const switchTo = async (m) => {
    if (m.is_active_for_user) { setOpen(false); return; }
    setSwitching(m.tenant_id);
    try {
      const { data } = await api.post("/memberships/switch", { tenant_id: m.tenant_id });
      setSession(data.token, data.user, data.tenant);
      toast.success(`Switched to ${data.tenant.name}`);
      setOpen(false);
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch");
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher-btn"
        className="w-full flex items-center gap-1.5 px-2 py-1 mt-2 text-xs border hover:bg-white transition-colors"
        style={{ borderColor: "var(--ll-border)" }}
      >
        <span className="flex-1 text-left truncate">Switch workspace</span>
        <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 top-full mt-1 border bg-white shadow-lg z-50 max-h-80 overflow-y-auto"
          style={{ borderColor: "var(--ll-ink)" }}
          data-testid="workspace-switcher-list"
        >
          {items.map((m) => {
            const active = m.is_active_for_user;
            const fresh = isRecentlySynced(m.stats?.last_connection_sync_at);
            const errors = m.stats?.sync_errors || 0;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => switchTo(m)}
                disabled={switching === m.tenant_id}
                data-testid={`switch-to-${m.tenant_id}`}
                className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 transition-colors ${active ? "bg-[var(--ll-mint)]" : "hover:bg-[var(--ll-bg-sidebar)]"}`}
              >
                <div className="relative shrink-0">
                  <Building2 size={12} />
                  {errors > 0 ? (
                    <span
                      className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-[var(--ll-ochre)] ring-2 ring-white animate-pulse"
                      data-testid={`sync-error-dot-${m.tenant_id}`}
                      title={`${errors} bank connection${errors === 1 ? "" : "s"} in error state`}
                    />
                  ) : fresh ? (
                    <span
                      className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-[#10B981] ring-2 ring-white animate-pulse"
                      data-testid={`fresh-dot-${m.tenant_id}`}
                      title="Bank synced in the last 24 hours"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-[var(--ll-ink)]">{m.tenant.name}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
                    {m.role} · {m.stats.bank_accounts} acct{m.stats.bank_accounts === 1 ? "" : "s"}
                    {errors > 0 && <span className="ml-1.5 text-[var(--ll-ochre-ink)]">· Sync error</span>}
                    {!errors && fresh && <span className="ml-1.5 text-[#059669]">· Fresh</span>}
                  </div>
                </div>
                {active && <CheckCircle2 size={12} className="text-[#10B981]" />}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/app/clients"); }}
            data-testid="switcher-open-portal"
            className="w-full px-3 py-2 text-left text-xs flex items-center gap-2 border-t hover:bg-[var(--ll-bg-sidebar)]"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <ExternalLink size={11} /> Open Clients portal
          </button>
        </div>
      )}
    </div>
  );
}
